import { zodResolver } from '@hookform/resolvers/zod';
import moment from 'moment';
import { FC, useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useRouter } from 'shared-routing';
import type { RecommendationItem } from 'voyager';
import { Recommendations, SpaDayDetailTemplate } from 'voyager';

import { BasketDrawerVersion } from '@common/components/BasketDrawer';
import { ContentParser } from '@common/components/ContentParser';
import { Spinner } from '@common/components/Spinner';
import { Routes } from '@common/constants';
import { normaliseGenderCode } from '@common/helpers/genderRestriction';
import { useLoadingState } from '@common/hooks';
import { JourneyPickerV2Connected } from '@features/JourneyPickerV2/container';
import {
  useGetSpaDaysPage,
  useGetSpaPackage,
  useGetSpaPackageTimeslots,
  useGetSpaSettings,
} from '@features/spa/hooks';
import { useGetSpaPackageExtras } from '@features/spa/hooks/useGetSpaPackageExtras';
import { useTranslations } from '@lang';
import { mergeCrossSellWithUpsell, parseDateToString } from '@utils/helpers';
import { mapDiscountToDisplayObject } from '@utils/mapDiscountToDisplayObject';
import { SpaPackageType } from 'shared-types/SpaPackage';

import { SpaPackageFormValues, buildSpaPackageFormSchema } from '../SpaPackageForm.schema';
import { SpaDayBooking } from './SpaDayBooking';
import { SpaDayExtras } from './SpaDayExtras';
import { SpaDayV2Props } from './SpaDayV2.types';

type CrossSellObject = {
  id: number | string;
  name?: string;
  image?: { url?: string };
  price?: { amount: number; currencyCode: string } | null;
  durationText?: string;
};

function mapCrossSellToRecommendations(
  crossSellItems: Array<{ type: string; object: CrossSellObject }>,
  fallbackImageUrl?: string
): RecommendationItem[] {
  return crossSellItems
    .filter(({ object }) => !!object?.name)
    .map(({ object }) => ({
      id: String(object.id),
      name: object.name!,
      imageUrl: object.image?.url || fallbackImageUrl,
      price: object.price ?? null,
      meta: object.durationText,
    }));
}

export const SpaDayV2: FC<SpaDayV2Props> = ({
  id,
  property,
  language,
  bookingStep,
  loggedInGuest,
  journeyParams,
}) => {
  const router = useRouter();
  const { t } = useTranslations();
  const [isBasketDrawerOpen, setIsBasketDrawerOpen] = useState(false);
  const startDate = moment(journeyParams?.spaDate).format('YYYY-MM-DD');

  const journeyBar = (
    <JourneyPickerV2Connected dateMode="single" dateContext="spa" showGuests numberOfMonths={1} />
  );

  const { data: spaPackage, isFetching } = useGetSpaPackage(property.id, id, language, false);

  const { isInitialLoading, isRefetching } = useLoadingState(isFetching, !!spaPackage);

  const { data: spaSettings } = useGetSpaSettings(property.id);
  const { data: spaDaysPage } = useGetSpaDaysPage(property.id, language);
  const fallbackImageUrl = spaDaysPage?.heroBackgroundImage?.url;

  const entity = spaPackage as SpaPackageType | undefined;

  const selectedDateString = moment(journeyParams?.spaDate).format('YYYY-MM-DD');

  const {
    data: timeslots,
    isFetching: isLoadingTimeslots,
    isError: isTimeslotsError,
  } = useGetSpaPackageTimeslots(
    property.id,
    id,
    selectedDateString,
    entity?.numberOfGuests ?? 1,
    1,
    !!entity
  );

  const isTelephoneOnly = entity?.telephoneOnly || false;
  const isUnavailable =
    !isTelephoneOnly &&
    !isLoadingTimeslots &&
    !!entity?.supportsTimeslots &&
    (isTimeslotsError || (!!timeslots && !timeslots[selectedDateString]?.length));

  const { data: extras = [] } = useGetSpaPackageExtras(
    property.id,
    id,
    language,
    selectedDateString
  );

  const genderRestriction = normaliseGenderCode(entity?.gender);
  const defaultGender =
    genderRestriction === 'M' ? 'male' : genderRestriction === 'F' ? 'female' : '';

  const initialGuestCount = entity
    ? Math.max(
        entity.numberOfGuests,
        Math.min(
          spaPackage?.maximumGuestsPerBasket || entity.numberOfGuests,
          journeyParams?.spaAdults || entity.numberOfGuests
        )
      )
    : journeyParams?.spaAdults || 1;

  const form = useForm<SpaPackageFormValues>({
    resolver: entity ? zodResolver(buildSpaPackageFormSchema(entity)) : undefined,
    defaultValues: {
      date: selectedDateString,
      guests: Array.from({ length: initialGuestCount }, () => ({
        title: '',
        firstName: '',
        lastName: '',
        gender: defaultGender,
      })),
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (form.getValues('date') !== selectedDateString) {
      form.setValue('date', selectedDateString, { shouldValidate: true });
    }
  }, [selectedDateString, form]);

  const handleCloseModal = useCallback(
    (showMiniBasket = false) => {
      router.push({ pathname: Routes.SPA_DAY_DETAIL, query: { id } });
      if (showMiniBasket) {
        setIsBasketDrawerOpen(true);
      }
    },
    [router, id]
  );

  const handleReserve = useCallback(() => {
    router.push({
      pathname: Routes.SPA_DAY_BOOKING,
      query: { id, bookingStep: 'availability' },
    });
  }, [router, id]);

  const handleSelectRecommendation = useCallback(
    (recId: string) => {
      router.push({ pathname: Routes.SPA_DAY_DETAIL, query: { id: recId } });
    },
    [router]
  );

  const handleBack = useCallback(() => {
    const isCategoryView = spaSettings?.isSpaDayCategoryViewVisible;
    const categoryId = spaPackage?.categories?.[0]?.id;

    if (isCategoryView && categoryId) {
      router.push({
        pathname: Routes.SPA_DAY_CATEGORY_DETAIL,
        query: { id: categoryId },
      });
    } else {
      router.push({
        pathname: Routes.SPA_DAY_LIST,
        query: { arrivalDate: parseDateToString(journeyParams?.spaDate) },
      });
    }
  }, [router, spaSettings, spaPackage, journeyParams?.spaDate]);

  const handleViewAllSpaDays = useCallback(() => {
    router.push({
      pathname: Routes.SPA_DAY_LIST,
      query: { arrivalDate: parseDateToString(journeyParams?.spaDate) },
    });
  }, [router, journeyParams?.spaDate]);

  const crossSellItems = mergeCrossSellWithUpsell(
    spaPackage?.upSell ?? null,
    'spa-day',
    spaPackage?.crossSellObjects ?? null
  );

  const recommendationItems = mapCrossSellToRecommendations(crossSellItems, fallbackImageUrl);

  const recommendations =
    recommendationItems.length > 0 ? (
      <Recommendations
        items={recommendationItems}
        onSelect={handleSelectRecommendation}
        title={t('product.otherProducts.title')}
        fromPriceLabel={t('common.from')}
        noImageLabel={t('spa.noImage')}
      />
    ) : undefined;

  const termsContent =
    spaPackage?.termsAndConditions || spaSettings?.termsAndConditions ? (
      <>
        {spaPackage?.termsAndConditions && <ContentParser text={spaPackage.termsAndConditions} />}
        {spaSettings?.termsAndConditions && <ContentParser text={spaSettings.termsAndConditions} />}
      </>
    ) : undefined;

  const propertyModules = Object.keys(property.modulesExcludingDisabled);
  const hideArrivalTimes = spaSettings?.hideArrivalTimes || false;

  const discount = mapDiscountToDisplayObject(spaPackage?.discount);

  const promotionName = spaPackage?.discount?.name;

  return (
    <>
      {!bookingStep && (
        <>
          <Spinner showDelayMessage isActive={isRefetching} />
          <SpaDayDetailTemplate
            imageUrl={spaPackage?.image?.url ?? null}
            imageAlt={spaPackage?.name}
            title={spaPackage?.name ?? ''}
            price={spaPackage?.price}
            discount={discount}
            priceLabel={t('common.from')}
            promotionName={promotionName}
            categoryLabel={spaPackage?.categories?.[0]?.name}
            description={spaPackage?.description}
            termsContent={termsContent}
            termsLabel={t('homepage.termsAndConditions')}
            onReserve={
              isTelephoneOnly || isUnavailable || isLoadingTimeslots ? undefined : handleReserve
            }
            reserveLabel={t('spa.book')}
            onBack={handleBack}
            recommendations={recommendations}
            isLoading={isInitialLoading}
            journeyBar={journeyBar}
            telephoneOnly={
              isTelephoneOnly
                ? {
                    title: t('spa.telephoneOnly', {
                      spaEntity: t('spa.spaDay').toLowerCase(),
                    }),
                    subtitle: t('treatment.callUsOn', {
                      phoneNumber: spaSettings?.phoneNumber,
                    }),
                  }
                : undefined
            }
            unavailable={
              isUnavailable
                ? {
                    title: t('spa.package.unavailableHeading'),
                    subtitle: t('spa.package.unavailableDescription', {
                      title: entity?.name,
                      date: moment(selectedDateString).format('dddd Do MMMM YYYY'),
                    }),
                    ctaLabel: t('common.backToModuleRoot', { entity: t('app.spaDays') }),
                    onCtaClick: handleViewAllSpaDays,
                  }
                : undefined
            }
          />
        </>
      )}
      {bookingStep === 'availability' && entity && (
        <FormProvider {...form}>
          <SpaDayBooking
            id={id}
            property={property}
            language={language}
            startDate={startDate}
            onClose={handleCloseModal}
            loggedInGuest={loggedInGuest}
          />
        </FormProvider>
      )}
      {bookingStep === 'extras' && entity && (
        <FormProvider {...form}>
          <SpaDayExtras
            extras={extras}
            entity={entity}
            property={property}
            language={language}
            id={id}
            onClose={handleCloseModal}
            onBack={() =>
              router.push({
                pathname: Routes.SPA_DAY_BOOKING,
                query: { id, bookingStep: 'availability' },
              })
            }
          />
        </FormProvider>
      )}
      <BasketDrawerVersion
        open={isBasketDrawerOpen}
        onClose={() => setIsBasketDrawerOpen(false)}
        propertyModules={propertyModules}
        hideArrivalTimes={hideArrivalTimes}
      />
    </>
  );
};
